import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { computeUserTier, TRIAL_DURATION_DAYS } from '../lib/trialCheck';
import { getRoutingDestination } from '../lib/harnessRouting';

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Get the full URL hash/query params
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);

      // Check for error in callback
      const errorDesc = hashParams.get('error_description') || queryParams.get('error_description');
      if (errorDesc) {
        setError(errorDesc);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
        return;
      }

      // Wait a moment for Supabase to process the session from URL hash
      // Supabase automatically parses the hash when getSession() is called
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
        setError('Sign in failed. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
        return;
      }

      if (session) {
        // Create/update profile but do NOT set subscribed — only Stripe webhook does that
        const userId = session.user.id;
        const email = session.user.email;
        try {
          await supabase.from('profiles').upsert({
            id: userId,
            email: email ?? '',
          }, { onConflict: 'id' });
        } catch (e) {
          console.error('[AuthCallback] Profile upsert failed:', e);
        }

        // Ensure trial dates are set for existing users who signed up before trial system
        const { data: trialProfile } = await supabase
          .from('profiles')
          .select('subscribed, trial_started_at, trial_expires_at')
          .eq('id', userId)
          .single();

        if (trialProfile && !trialProfile.trial_started_at && !trialProfile.trial_expires_at && !trialProfile.subscribed) {
          await supabase.from('profiles').update({
            trial_started_at: new Date().toISOString(),
            trial_expires_at: new Date(Date.now() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000).toISOString(),
            trial_used: true,
          }).eq('id', userId);
        }

        // Route based on subscription/trial status
        const { data: profile } = await supabase
          .from('profiles')
          .select('subscribed, trial_expires_at')
          .eq('id', userId)
          .single();

        if (profile) {
          const tier = computeUserTier(profile);
          if (tier === 'subscribed' || tier === 'trial-active') {
            // Route based on v2 flag: flag ON → /session/new, flag OFF → /app
            const routing = await getRoutingDestination(userId);
            if (routing.destination === '/app' || routing.destination === '/chat') {
              navigate('/app', { replace: true });
            } else if (routing.sessionId) {
              navigate(`/session/${routing.sessionId}`, { replace: true });
            } else {
              navigate('/app', { replace: true });
            }
          } else {
            navigate('/pricing?trial_expired=true', { replace: true });
          }
        } else {
          navigate('/pricing?welcome=true', { replace: true });
        }
      } else {
        // No session found, redirect to login
        navigate('/login', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-csai-bg)]">
        <div className="text-center">
          <p className="text-red-600 mb-2">{error}</p>
          <p className="text-[var(--color-csai-muted)]">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-csai-bg)]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-csai-primary)] mx-auto mb-4"></div>
        <p className="text-[var(--color-csai-muted)]">Completing sign in...</p>
      </div>
    </div>
  );
}
